// ============================================================
// Store global con Zustand — estado compartido de la app
// ============================================================

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Usuario, Modalidad } from '@/types'
import {
  DEFAULT_VOICE_SHORTCUTS,
  normalizeVoiceShortcuts,
  type VoiceShortcutConfig,
} from '@/hooks/voiceShortcuts'
import { applyThemeClass, type TemaPreferencia } from '@/lib/themePreference'

interface AppState {
  // Usuario
  usuario: Usuario | null
  setUsuario: (usuario: Usuario | null) => void

  // Preferencias UI
  tema: TemaPreferencia
  setTema: (tema: TemaPreferencia) => void
  tamanoFuente: 'sm' | 'md' | 'lg'
  setTamanoFuente: (tamano: 'sm' | 'md' | 'lg') => void
  voiceShortcuts: VoiceShortcutConfig
  setVoiceShortcuts: (shortcuts: VoiceShortcutConfig) => void
  voiceAutoPunctuation: boolean
  setVoiceAutoPunctuation: (enabled: boolean) => void
  organizacionActivaId: string | null
  setOrganizacionActivaId: (organizationId: string | null) => void

  // Módulo de reporte
  modalidadActiva: Modalidad
  setModalidadActiva: (modalidad: Modalidad) => void

  // Contador de uso diario (sincronizado con BD)
  reportesHoy: number
  consultasHoy: number
  incrementarReportesHoy: () => void
  incrementarConsultasHoy: () => void
  resetContadores: () => void
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      // Usuario
      usuario: null,
      setUsuario: (usuario) => set(prev => ({
        usuario,
        organizacionActivaId: prev.usuario?.id === usuario?.id ? prev.organizacionActivaId : null,
      })),

      // Tema
      tema: 'oscuro',
      setTema: (tema) => {
        set({ tema })
        applyThemeClass(document.documentElement, tema)
      },

      // Fuente
      tamanoFuente: 'md',
      setTamanoFuente: (tamanoFuente) => set({ tamanoFuente }),
      voiceShortcuts: DEFAULT_VOICE_SHORTCUTS,
      setVoiceShortcuts: (voiceShortcuts) => set({
        voiceShortcuts: normalizeVoiceShortcuts(voiceShortcuts),
      }),
      voiceAutoPunctuation: false,
      setVoiceAutoPunctuation: (voiceAutoPunctuation) => set({ voiceAutoPunctuation }),
      organizacionActivaId: null,
      setOrganizacionActivaId: (organizacionActivaId) => set({ organizacionActivaId }),

      // Modalidad activa
      modalidadActiva: 'RX',
      setModalidadActiva: (modalidadActiva) => set({ modalidadActiva }),

      // Contadores
      reportesHoy: 0,
      consultasHoy: 0,
      incrementarReportesHoy: () => set(prev => ({ reportesHoy: prev.reportesHoy + 1 })),
      incrementarConsultasHoy: () => set(prev => ({ consultasHoy: prev.consultasHoy + 1 })),
      resetContadores: () => set({ reportesHoy: 0, consultasHoy: 0 }),
    }),
    {
      name: 'radcore-v2-store',
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<AppState> | undefined
        return {
          ...currentState,
          ...persisted,
          voiceShortcuts: normalizeVoiceShortcuts(persisted?.voiceShortcuts),
        }
      },
      // Solo persistir preferencias de UI, no datos sensibles
      partialize: (state) => ({
          tema: state.tema,
          tamanoFuente: state.tamanoFuente,
          voiceShortcuts: state.voiceShortcuts,
          voiceAutoPunctuation: state.voiceAutoPunctuation,
          organizacionActivaId: state.organizacionActivaId,
          modalidadActiva: state.modalidadActiva,
      }),
    }
  )
)
